import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Stack, Typography } from '@mui/material';
import React from 'react';
import { DialogPromptSpeakNoGrade } from '../../api/DialogPromptApi';
import ChatBubble from '../ChatBubble';

import MicrophoneBlueIcon from '../../asset/icon-microphone-blue.png';
import AudioWaveIcon from '../../asset/icons8-audio-wave.gif';

import { useHistory, useLocation } from 'react-router-dom';
import { useAudioRecorder } from '../../AudioRecorder';
import { AudioApi } from '../../api/AudioApi';
import { Sentence, SentenceApi } from '../../api/SentenceApi';
import AppBarContext from '../../context/AppBarContext';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import { scrollElementIntoViewWithFallback } from '../../util/scrollUtils';
import { handleErrorMessage } from '../ErrorDialog';
import { TeacherAudioActions, getSelectedTeacherSpeakerRole } from '../TeacherAudioReportButton';
import { getAudioByText } from './DialogPromptComponent';

interface DialogPromptSpeakNoGradeComponentProps {
    dialogPrompt: DialogPromptSpeakNoGrade;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}


const DialogPromptSpeakNoGradeComponent: React.FC<DialogPromptSpeakNoGradeComponentProps> = ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const { playAudio } = useAudioPlayer();

    const { setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

    const [teacherChinesePromptSentence, setTeacherChinesePromptSentence] = React.useState<Sentence | null>(null);

    const [promptAudio, setPromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [teacherChinesePromptAudio, setTeacherChinesePromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [, setStudentResponsePromptAudio] = React.useState<Blob | null | undefined>(undefined);
    const [yesResponseAudio, setYesResponseAudio] = React.useState<Blob | null | undefined>(undefined);

    React.useEffect(() => {
        clearFooterList()

        if (dialogPrompt.teacher_chinese_prompt_sentence_id) {
            SentenceApi.getById(dialogPrompt.teacher_chinese_prompt_sentence_id)
                .then((response) => {
                    setTeacherChinesePromptSentence(response.data)
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                })
        }
        getAudioByText(history, setErrorMessage, dialogPrompt.prompt, setPromptAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.teacher_chinese_prompt, setTeacherChinesePromptAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.student_response_prompt, setStudentResponsePromptAudio)
        getAudioByText(history, setErrorMessage, dialogPrompt.yes_response, setYesResponseAudio)

    }, [clearFooterList, dialogPrompt.teacher_chinese_prompt_sentence_id, dialogPrompt.prompt, dialogPrompt.teacher_chinese_prompt, dialogPrompt.student_response_prompt, dialogPrompt.yes_response, history, location, setErrorMessage])

    const [teacherEnglishChatBubbleDone, setTeacherEnglishChatBubbleDone] = React.useState(false);
    const [teacherImageDone, setTeacherImageDone] = React.useState(false);

    const imageRef = React.useRef<HTMLImageElement>(null);

    const [imageSize, setImageSize] = React.useState<{ width: number; height: number } | null>(null);
    const [showLightbox, setShowLightbox] = React.useState(false);
    const [hovering, setHovering] = React.useState(false);

    React.useEffect(() => {
        if (teacherEnglishChatBubbleDone) {
            // console.log("teacherEnglishChatBubbleDone=true");
            if ((dialogPrompt.image_src && dialogPrompt.image_src != "broken") || (dialogPrompt.image_src_url && dialogPrompt.image_src_url != "broken")) {
                // console.log("dialogPrompt.image_src")
                // make sure to scroll after the image is loaded.
                const handleImageLoad = () => {
                    setTeacherImageDone(true);
                };

                const imageEl = imageRef.current;
                if (imageEl) {
                    if (imageEl.complete && imageEl.naturalHeight !== 0) {
                        // If image is already loaded
                        handleImageLoad();
                    } else {
                        // Image is not yet loaded, attach event listener
                        imageEl.addEventListener('load', handleImageLoad);
                    }
                }

                return () => {
                    // console.log("dialogPrompt.image_src return")
                    if (imageEl) {
                        // console.log("dialogPrompt.image_src return removeEventListener")
                        imageEl.removeEventListener('load', handleImageLoad);
                    }
                };
            } else {
                // console.log("setTeacherImageDone(true)")
                setTeacherImageDone(true);
            }
        }
    }, [teacherEnglishChatBubbleDone, dialogPrompt.image_src, dialogPrompt.image_src_url])


    const [teacherChineseChatBubbleDone, setTeacherChineseChatBubbleDone] = React.useState(false);

    const handleTeacherChinesePromptSpeakerClick = React.useCallback(() => {
        if (teacherChinesePromptSentence == null) return;

        const audioPromise = AudioApi.getTeacherSentence(teacherChinesePromptSentence.id)

        audioPromise
            .then(response => {
                // check if the response was successful
                if (response.status >= 300) {
                    throw new Error('Getting male audio was not ok statuscode=' + response.status + " " + response.statusText);
                }
                return response.data;
            })
            .then(blob => {
                playAudio(blob)
                setTeacherChineseChatBubbleDone(true)
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [teacherChinesePromptSentence, playAudio, setErrorMessage, history, location]);

    const teacherChineseAudioReportContext = React.useMemo(() => teacherChinesePromptSentence ? ({
        sentenceId: teacherChinesePromptSentence.id,
        dialogPromptId: dialogPrompt.id,
        textToSpeechSpeakerId: getSelectedTeacherSpeakerRole(),
        audioText: teacherChinesePromptSentence.text,
        audioRole: 'teacher_chinese_prompt',
    }) : null, [dialogPrompt.id, teacherChinesePromptSentence]);

    React.useEffect(() => {
        if (teacherImageDone) {
            // console.log("teacherImageDone=true");
            if (dialogPrompt.teacher_chinese_prompt_sentence_id && teacherChinesePromptSentence == null) {
                return;
            }
            if (teacherChinesePromptSentence && teacherChinesePromptSentence.text) {
                setActionList([
                    <TeacherAudioActions
                        context={teacherChineseAudioReportContext}
                        onPlay={handleTeacherChinesePromptSpeakerClick}
                        showReportMenu={false}
                    />
                ])
            } else {
                setTeacherChineseChatBubbleDone(true)
            }
        }
    }, [teacherImageDone, teacherChinesePromptSentence, dialogPrompt.teacher_chinese_prompt_sentence_id, handleTeacherChinesePromptSpeakerClick, setActionList, teacherChineseAudioReportContext])

    function isStudentResponsePromptEmpty(prompt: string | null) {
        return prompt == null || prompt == undefined || prompt.trim().toLowerCase() == "<answer/>"
    }

    React.useEffect(() => {
        if (teacherChineseChatBubbleDone) {
            // console.log("teacherChineseChatBubbleDone true")
            if (isStudentResponsePromptEmpty(dialogPrompt.student_response_prompt)) {
                // skip student chat bubble if there is none
                setStudentChatBubbleDone(true);
            }
        }
    }, [teacherChineseChatBubbleDone, dialogPrompt.student_response_prompt])

    const [studentChatBubbleDone, setStudentChatBubbleDone] = React.useState(false);

    React.useEffect(() => {
        if (studentChatBubbleDone) {
            // console.log("studentChatBubbleDone=true");
            // need to scroll after this effect for some reason.
            // scrolling didn't work after teacherChineseChatBubbleDone
            if (imageRef.current) {
                // console.log("studentChatBubbleDone imageRef scrollIntoView")
                scrollElementIntoViewWithFallback(imageRef.current);
            }
            if (chatBubbleContainerRef.current) {
                scrollElementIntoViewWithFallback(chatBubbleContainerRef.current);
                // console.log("studentChatBubbleDone, chatBubbleContainerRef, scrollIntoView")
            }
        }
    }, [studentChatBubbleDone, chatBubbleContainerRef])


    // const [isRecording, setIsRecording] = React.useState(false);
    const [studentAudioDone, setStudentAudioDone] = React.useState(false);
    const [chatBubbleContinueDone, setChatBubbleContinueDone] = React.useState(false);


    const { startRecording, stopRecording, isRecording } = useAudioRecorder({
        setErrorMessage,
        onStop: () => {
            setStudentAudioDone(true);
            // setIsScoring(true)
            // const formData = new FormData();
            // formData.append('audio', audioData);

            // ScoreApi.getScore(dialogPrompt.sentence_id, formData)
            //     .then(response => {
            //         setIsScoring(false)
            //         // check if the response was successful
            //         if (response.status >= 300) {
            //             throw new Error('Scoring audio was not ok statuscode=' + response.status + " " + response.statusText);
            //         }
            //         return response.data;
            //     })
            //     .then(responseJson => {
            //         responseJson.audioData = audioData;
            //         setSentenceScore(responseJson);
            //     })
            //     .catch(err => {
            //         handleErrorMessage(err, setErrorMessage, history, location);
            //     })
        }
    });


    React.useEffect(() => {
        if (studentAudioDone || chatBubbleContinueDone) {
            setActionList([]);
            return;
        }

        if (studentChatBubbleDone) {
            // no skip button.
            // setLeftNavigationList([
            //     <IconButton onClick={handleCancelClick}>
            //         <img src={RightArrowBlueIcon} style={{ height: '100%', width: 'auto', maxHeight: '24px' }} />
            //     </IconButton>
            // ])
            setActionList([
                <IconButton
                    aria-label={isRecording ? "Stop recording" : "Start recording"}
                    title={isRecording ? "Stop recording" : "Start recording"}
                    onClick={isRecording ? stopRecording : startRecording}
                >
                    {isRecording ? <img src={AudioWaveIcon} alt="Recording" style={{ height: '100%', width: 'auto', maxHeight: '36px' }} />
                        : <img src={MicrophoneBlueIcon} alt="Microphone" style={{ height: '100%', width: 'auto', maxHeight: '36px' }} />}
                </IconButton>
            ])
        }
    }, [studentChatBubbleDone, studentAudioDone, chatBubbleContinueDone, isRecording, setActionList, startRecording, stopRecording]);

    // const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);

    // let audioData: Blob | null = null;


    // const startRecording = () => {
    //     navigator.mediaDevices.getUserMedia({ audio: true })
    //         .then((stream) => {
    //             // console.log("startRecording() 1")
    //             mediaRecorderRef.current = new MediaRecorder(stream);
    //             mediaRecorderRef.current.addEventListener('dataavailable', handleAudioDataAvailable);
    //             mediaRecorderRef.current.addEventListener('stop', handleAudioDone);

    //             mediaRecorderRef.current.start();

    //             setIsRecording(true);
    //             // console.log("startRecording() 2")
    //         })
    //         .catch(err => {
    //             handleErrorMessage(err, setErrorMessage, history, location);
    //         });
    // };
    // const stopRecording = () => {
    //     if (mediaRecorderRef.current) {
    //         // console.log("stopRecording() 1")
    //         mediaRecorderRef.current.stop();
    //         mediaRecorderRef.current.removeEventListener('dataavailable', handleAudioDataAvailable);
    //         mediaRecorderRef.current.removeEventListener('stop', handleAudioDone);
    //         // console.log("stopRecording() 2")
    //     }

    //     setIsRecording(false);
    // };

    // const handleAudioDataAvailable = (event: BlobEvent) => {
    //     // console.log("handleAudioDataAvailable() 1")
    //     if (event.data.size > 0) {
    //         // console.log("event.data.size > 0")
    //         audioData = event.data;
    //     }
    // };

    // const handleAudioDone = () => {
    //     // console.log("handleAudioDone() 1")
    //     if (audioData) {
    //         setStudentAudioDone(true);
    //         // @TODO save audio somehow. in addition to endpoint, also need need new DB tables I think
    //     }
    // }

    const [openCancelDialog, setOpenCancelDialog] = React.useState(false);

    const handleCancelDialogConfirm = () => {
        setOpenCancelDialog(false);
        setChatBubbleContinueDone(true);
    };

    const handleCancelDialogCancel = () => {
        setOpenCancelDialog(false);
    };

    React.useEffect(() => {
        if (chatBubbleContinueDone) {
            clearFooterList()
            setDialogPromptComplete(dialogPrompt.id)

        }
    }, [chatBubbleContinueDone, clearFooterList, dialogPrompt.id, setDialogPromptComplete])

    return (
        <Stack>
            <ChatBubble
                isTeacher={dialogPrompt.is_teacher}
                message={dialogPrompt.prompt}
                messageAudio={promptAudio ? promptAudio : null}
                showAvatar={showAvatar}
                chatBubbleContainerRef={chatBubbleContainerRef}
                setChatBubbleDone={setTeacherEnglishChatBubbleDone}
            />
            {(teacherEnglishChatBubbleDone && (dialogPrompt.image_src || dialogPrompt.image_src_url)) && (<>
                <div style={{ position: 'relative', display: 'inline-block', margin: '0 auto' }}>
                    <img
                        ref={imageRef}
                        src={dialogPrompt.image_src ? dialogPrompt.image_src : (dialogPrompt.image_src_url ? dialogPrompt.image_src_url : undefined)}
                        alt={dialogPrompt.teacher_chinese_prompt ? dialogPrompt.teacher_chinese_prompt : ""}
                        onClick={() => setShowLightbox(true)}
                        onMouseEnter={() => setHovering(true)}
                        onMouseLeave={() => setHovering(false)}
                        onLoad={(e) => {
                            const img = e.currentTarget;
                            setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
                        }}
                        style={{
                            maxHeight: '200px', maxWidth: '200px',
                            height: 'auto', width: 'auto',
                            display: 'block',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s ease',
                            opacity: hovering ? 0.85 : 1,
                            borderRadius: '10px',
                        }}
                    />
                    {hovering && imageSize && (
                        <div style={{
                            position: 'absolute', bottom: '8px', right: '8px',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)', color: '#fff',
                            padding: '4px 8px', borderRadius: '4px',
                            fontSize: '12px', pointerEvents: 'none', zIndex: 10,
                        }}>
                            {imageSize.width} × {imageSize.height}
                        </div>
                    )}
                </div>
            </>)}
            {(teacherImageDone && teacherChinesePromptSentence && teacherChinesePromptSentence.text) && (<>
                <ChatBubble
                    isTeacher={true}
                    message={teacherChinesePromptSentence && teacherChinesePromptSentence.text ? teacherChinesePromptSentence.text : ''}
                    messageAudio={teacherChinesePromptAudio ? teacherChinesePromptAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    useChineseFont={true}
                />
            </>)}
            {(teacherChineseChatBubbleDone && !isStudentResponsePromptEmpty(dialogPrompt.student_response_prompt)) && (
                <ChatBubble
                    isTeacher={false}
                    message={dialogPrompt.student_response_prompt ? dialogPrompt.student_response_prompt : ""}
                    messageAudio={null} // no audio for student
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    setChatBubbleDone={setStudentChatBubbleDone}
                    useChineseFont={true}
                />
            )}
            {studentAudioDone && (
                <ChatBubble
                    isTeacher={true}
                    message={dialogPrompt.yes_response}
                    messageAudio={yesResponseAudio ? yesResponseAudio : null}
                    showAvatar={showAvatar}
                    chatBubbleContainerRef={chatBubbleContainerRef}
                    setChatBubbleDone={setChatBubbleContinueDone}
                />
            )}

            {/* Lightbox */}
            {showLightbox && (
                <div
                    aria-label="Close image preview"
                    onClick={() => setShowLightbox(false)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            setShowLightbox(false);
                        }
                    }}
                    role="button"
                    tabIndex={0}
                    style={{
                        position: 'fixed', top: 0, left: 0,
                        width: '100vw', height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', zIndex: 9999, margin: 0, padding: 0,
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', cursor: 'default' }}
                    >
                        <img
                            src={dialogPrompt.image_src ? dialogPrompt.image_src : (dialogPrompt.image_src_url ? dialogPrompt.image_src_url : undefined)}
                            alt={dialogPrompt.teacher_chinese_prompt ? dialogPrompt.teacher_chinese_prompt : ""}
                            onLoad={(e) => {
                                const img = e.currentTarget;
                                setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
                            }}
                            style={{
                                maxWidth: '90vw', maxHeight: '90vh',
                                objectFit: 'contain', borderRadius: '10px',
                                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.5)',
                            }}
                        />
                        {imageSize && (
                            <div style={{ color: '#ccc', fontSize: '12px', paddingRight: '4px' }}>
                                {imageSize.width} × {imageSize.height}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Confirmation Dialog */}
            <Dialog open={openCancelDialog} onClose={handleCancelDialogCancel}>
                <DialogTitle><Typography variant="h4" sx={{ color: '#383838' }}>Confirm Skip</Typography></DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#8D8D8D' }}>
                        Are you sure you want to skip this exercise?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', '& .MuiButton-root': { flex: 1 } }}>
                    <Button onClick={handleCancelDialogCancel} color="primary" sx={{ color: '#787878' }}>
                        Keep trying
                    </Button>
                    <Button onClick={handleCancelDialogConfirm} color="primary" sx={{ color: '#787878' }}>
                        Skip
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack >
    );
};

export default DialogPromptSpeakNoGradeComponent;
